/**
 * HQ 태블릿·게이트 원격 관리 서비스 (2026-10-28, 대표 전용 — 백엔드에서 hq_admin 게이트).
 * 태블릿(앱 5.7.6~)이 90초마다 보고 → backend/api/tablet_gate_remote.py → 이 화면.
 */
import hqApi from './hqApiClient';

export interface GateCheck {
  ip: string | null;
  ok: boolean | null;
  checked_at?: number | null;
}

export interface ApplyResult {
  version?: string | null;
  ok?: boolean | null;
  reverted?: boolean | null;
  detail?: string | null;
  at?: number | null;
}

export interface TabletReport {
  device_id: string;
  app_version?: string | null;
  tablet_role?: string | null;
  entry?: GateCheck | null;
  exit?: GateCheck | null;
  wifi_ip?: string | null;
  wifi_ssid?: string | null;
  applied_version?: string | null;
  last_apply?: ApplyResult | null;
  scanning?: boolean;
  reported_at: number;
  seconds_ago: number;
  fresh: boolean;
  via?: string;
}

export interface GateServerState {
  linked: boolean;
  seconds_ago: number | null;
  version?: string | null;
  reported_ip?: string | null;
  /** 게이트가 폴링 때 알려 준 매장 안 주소(역할→칸). KC868 v2.3+ 만 보낸다 */
  reported_ips?: { entry?: string; exit?: string };
  /** true=알려 줌 / false=게이트는 붙었는데 안 알려 줌(옛 펌웨어) / null=게이트 소식 없음 */
  reports_ip?: boolean | null;
  alive_roles?: string[];
  roles?: Record<string, { ip: string | null; version: string | null; seconds_ago: number }>;
}

/** 태블릿 직접 열기 실패 → 서버 경유 우회 열기 기록 (2026-10-28) */
export interface BypassItem {
  at: number;
  result: 'ok' | 'not_linked' | 'not_taken' | 'busy' | string;
  direction?: string;
  device_id?: string | null;
  app_version?: string | null;
  direct_ip?: string | null;
  direct_error?: string | null;
  direct_ms?: number | null;
  ms?: number | null;
  source?: string | null;
  via?: string;
}

export interface BypassSummary {
  today: number;
  today_fail: number;
  week: number;
  per_day: { date: string; ok: number; fail: number }[];
  last_at: number | null;
  last_result: string | null;
  log?: BypassItem[];
}

export interface StoreRow {
  organization_id: string;
  organization_name: string;
  gate_server: GateServerState;
  saved: { entry_ip: string; exit_ip: string };
  addr_mismatch: string[];
  tablets: { total: number; fresh: number; gate_ok: number; gate_down: number; drift: number } | null;
  bypass: BypassSummary | null;
}

export interface FoundGate {
  ip: string;
  mdns?: string | null;
  role?: string | null;
  store_name?: string | null;
  org_id?: string | null;
  version?: string | null;
  seen_by: string[];
  store_match: boolean | null;
}

export interface HistoryItem {
  at: number;
  kind: 'config_changed' | 'scan_requested' | 'tablet_apply_ok' | 'tablet_apply_failed' | string;
  by?: string;
  device_id?: string;
  version?: string;
  before?: { entry_ip: string; exit_ip: string };
  after?: { entry_ip: string; exit_ip: string };
  reverted?: boolean;
  detail?: string | null;
  confirm_store_mismatch?: boolean;
}

export interface StoreDetail {
  redis_ok: boolean;
  organization_id: string;
  organization_name: string;
  gate_server: GateServerState;
  addr_mismatch: string[];
  bypass: BypassSummary | null;
  saved: {
    entry_ip: string;
    exit_ip: string;
    open_duration_ms: number;
    updated_at: string | null;
    config_version: string | null;
  };
  tablets: TabletReport[] | null;
  old_app_tablets: { device_id: string; last_at: string | null; app_version: string | null }[];
  scan: { id: string; requested_by: string; at: number } | null;
  scan_results: {
    tablets: {
      device_id: string;
      wifi_ip?: string | null;
      subnets?: string[];
      error?: string | null;
      finished_at?: number | null;
      count: number;
    }[];
    found: FoundGate[];
  } | null;
  history: HistoryItem[] | null;
}

const hqTabletGateService = {
  async listStores(): Promise<{ redis_ok: boolean; stores: StoreRow[] }> {
    const { data } = await hqApi.get('/api/v1/hq/tablet-gates/stores');
    return data;
  },
  async getStore(orgId: string): Promise<StoreDetail> {
    const { data } = await hqApi.get(`/api/v1/hq/tablet-gates/stores/${orgId}`);
    return data;
  },
  async requestScan(orgId: string) {
    const { data } = await hqApi.post(`/api/v1/hq/tablet-gates/stores/${orgId}/scan`);
    return data as { ok: boolean; scan: { id: string } };
  },
  async saveConfig(orgId: string, body: { entry_ip: string; exit_ip: string; confirm_store_mismatch?: boolean }) {
    const { data } = await hqApi.put(`/api/v1/hq/tablet-gates/stores/${orgId}/gate-config`, body);
    return data as {
      ok: boolean;
      pushed: boolean;
      version?: string;
      message?: string;
      before: { entry_ip: string; exit_ip: string };
      after: { entry_ip: string; exit_ip: string };
    };
  },
};

export default hqTabletGateService;
